import { Component, Input, OnInit, ViewChild } from '@angular/core';
import { ApiService } from '../../../services/api.service';
import { UiService } from '../../../services/ui.service';
import { TranslocoService } from '@ngneat/transloco';
import { HelperService } from '../../../services/helper.service';
import moment from 'moment';
import { Table } from 'primeng/table';
import { PeriodSelectType } from '../reporting-date-selector/reporting-date-selector.component';
import { FilterService } from 'primeng/api';

@Component({
  selector: 'app-reporting-table',
  templateUrl: './reporting-table.component.html',
  styleUrls: ['./reporting-table.component.scss']
})
export class ReportingTableComponent implements OnInit {

  @Input() type: string = null;
  @ViewChild('dt') table: Table;

  public loading = false;
  public columns: Array<any> = [];
  public exportColumns: Array<any> = [];
  public entries: Array<any> = [];
  public filtered: Array<any> = [];

  public dateSelectorVisible = false;
  public dateSelectorField = null;
  public dateFilterValues: { [key: string]: { start: Date | null, end: Date | null } } = {};

  constructor(
    private api: ApiService,
    private ui: UiService,
    private transloco: TranslocoService,
    private helper: HelperService,
    private filterService: FilterService
  ) { }

  ngOnInit(): void {
    this.filterService.register('dateBetween', (value: Date, filter: PeriodSelectType): boolean => {
      if (filter === null || (filter.start === null && filter.end === null)) {
        return true;
      }
      const date = moment(value);
      if (
        (filter.start === null || moment(filter.start).isSameOrBefore(date)) &&
        (filter.end === null || moment(filter.end).isSameOrAfter(date))
      ) {
        return true;
      }
      return false;
    });

    if (this.type !== null) {
      this.loadData();
    }
  }

  public loadData(): void {
    this.loading = true;
    this.api.getReporting(this.type).subscribe({
      next: (result: any) => {
        this.columns = result.data?.columns || [];
        this.entries = result.data?.entries?.map((entry: any) => {
          return this.getMappedEntry(entry);
        }) || [];
        this.filtered = [...this.entries];
      },
      error: () => {}
    }).add(() => {
      this.loading = false;
    });
  }

  public loadExport(): void {
    this.exportColumns = [...this.columns].sort((a: any, b: any) => {
      return (a.export_position > b.export_position) ? 1 : ((b.export_position > a.export_position) ? -1 : 0);
    });
    const exportData = this.filtered.map((entry: any) => {
      return this.getMappedEntryForExport(entry);
    });
    if (exportData.length === 0) {
      this.ui.toastInfo(this.transloco.translate(this.transloco.translate('reportings_date_filter.messages.no_results')));
    } else {
      import('xlsx').then(xlsx => {
          const worksheet = xlsx.utils.json_to_sheet(exportData);
          const workbook = { Sheets: { data: worksheet }, SheetNames: ['data'] };
          const excelBuffer: any = xlsx.write(workbook, { bookType: 'xlsx', type: 'array' });
          this.helper.saveAsExcelFile(excelBuffer, 'report_' + this.type);
      });
    }
  }

  public onFilter(event: any): void {
    this.filtered = [...event.filteredValue];
  }

  public showDateSelector(field: string): void {
    this.dateSelectorVisible = true;
    this.dateSelectorField = field;
  }

  public filterPeriod(data: PeriodSelectType): void {
    this.dateSelectorVisible = false;
    this.dateFilterValues[this.dateSelectorField] = {
      start: data.start,
      end: data.end,
    };
    this.table.filter(this.dateFilterValues[this.dateSelectorField], this.dateSelectorField, 'dateBetween');
  }

  public clearPeriod(field: string): void {
    this.dateFilterValues[field] = undefined;
    this.table.filter(null, this.dateSelectorField, 'dateBetween');
  }

  private getMappedEntry(entry: any): any {
    const result: any = {};
    this.columns.forEach((column: any) => {
      const value = entry[column.name];
      if (Array.isArray(value)) {
        const subcontent: Array<any> = [];
        value.forEach((entry: any) => {
          subcontent.push(this.getUiFormattedValue(entry, column.format));
        });
        result[column.name] = subcontent.join(', ');
      } else {
        result[column.name] = this.getUiFormattedValue(value, column.format);
      }
    });
    result.hyperlink = entry.hyperlink || null;
    return result;
  }

  private getUiFormattedValue(value: any, format: string): any {
    switch (format) {
      case 'date': {
        return (value) ? moment(value).toDate() : '';
      }
      case 'translate': {
        return this.transloco.translate(value);
      }
      case 'language': {
        const lang = this.transloco.getActiveLang().toUpperCase();
        return (value[lang]) ? value[lang] : '';
      }
    }
    return value;
  }

  private getMappedEntryForExport(entry: any): any {
    const result = {};
    this.exportColumns.forEach((column: any) => {
      const COL_NAME = (column.excelname) ? this.transloco.translate('reportings.labels.' + column.excelname) : this.transloco.translate('reportings.labels.' + column.name);
      const value = entry[column.name];
      if (Array.isArray(value)) {
        const subcontent: Array<any> = [];
        value.forEach((entry: any) => {
          subcontent.push(this.getExcelFormattedValue(entry, column.format));
        });
        result[COL_NAME] = subcontent.join(', ');
      } else {
        result[COL_NAME] = this.getExcelFormattedValue(value, column.format);
      }
    });
    return result;
  }

  private getExcelFormattedValue(value: any, format: string): any {
    switch (format) {
      case 'date': {
        return (value) ? moment(value).toDate() : '';
      }
    }
    return value;
  }


}
